import React, { useState, useEffect, useRef, useCallback } from 'react';
import './vertical-range-selector.scss'; 
import { on } from 'events';
import DepthTrack from './depth-track';

const VerticalRangeSelector = ({
  containerHeight,   
  minRangePercent,
  onRangeChange,
  setIsDragging,
  disabled,
  startPercent,
  endPercent,
  startDepth,
  endDepth,
  depthUnit,
  minHeaderHeight,
  headerBottomPadding,
  availableHeight,
  trackTop,
  topMargin,
  depthMajorIntervals,
  setDialogMajorIntervals,
  showDepthGridLines,
  setShowDepthGridlines,
  depthMinorIntervals,
  setDepthMinorIntervals,
  showDepthMinorIntervals,
  setShowDepthMinorIntervals
}: any) => {

  const [isDraggingTop, setIsDraggingTop] = useState(false);
  const [isDraggingBottom, setIsDraggingBottom] = useState(false);
  const [isDraggingRange, setIsDraggingRange] = useState(false);

  const selectorRef = useRef<any>(null);
  const dragStartRef = useRef<any>({ y: 0, originalStart: 0, originalEnd: 0 });

  const clamp = (value: any, min = 0, max = 100) => Math.min(Math.max(value, min), max);

  const handleMouseDownTop = (e: any) => {
    e.preventDefault();    
    if (disabled) return;
    setIsDraggingTop(true);
    if (setIsDragging) {
      setIsDragging(true);
    }
    
    dragStartRef.current = { y: e.clientY, originalStart: startPercent, originalEnd: endPercent };
  };

  const handleMouseDownBottom = (e: any) => {
    e.preventDefault();
    if (disabled) return;
    setIsDraggingBottom(true);
    if (setIsDragging) {
      setIsDragging(true);
    }
    
    dragStartRef.current = { y: e.clientY, originalStart: startPercent, originalEnd: endPercent };
  };

  const handleMouseDownRange = (e: any) => {
    // Only start range drag if not clicking on a handle
    if (e.target === e.currentTarget) {        
        e.preventDefault();
        if (disabled) return;
        setIsDraggingRange(true);
        if (setIsDragging) {
          setIsDragging(true);
        }

        dragStartRef.current = { y: e.clientY, originalStart: startPercent, originalEnd: endPercent };
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingTop && !isDraggingBottom && !isDraggingRange) return;
    if (!selectorRef.current) return;

    const selectorRect = selectorRef.current.getBoundingClientRect();
    const mouseYRelativeToSelector = e.clientY - selectorRect.top;
    let newPercent = (mouseYRelativeToSelector / containerHeight) * 100;

    if (isDraggingTop) {
      let newStart = clamp(newPercent);
      if (newStart > endPercent - minRangePercent) {
        newStart = endPercent - minRangePercent;
      }

      onRangeChange(clamp(newStart), endPercent);      
    } else if (isDraggingBottom) {
      let newEnd = clamp(newPercent);
      if (newEnd < startPercent + minRangePercent) {
        newEnd = startPercent + minRangePercent;
      }
      
      onRangeChange(startPercent, clamp(newEnd));
    } else if (isDraggingRange) {
      const deltaY = e.clientY - dragStartRef.current.y;
      const deltaPercent = (deltaY / containerHeight) * 100;

      let newStart = dragStartRef.current.originalStart + deltaPercent;
      let newEnd = dragStartRef.current.originalEnd + deltaPercent;
      const rangeDuration = dragStartRef.current.originalEnd - dragStartRef.current.originalStart;

      // Clamp to container boundaries while maintaining range duration
      if (newStart < 0) {
        newStart = 0;
        newEnd = rangeDuration;
      }
      if (newEnd > 100) {
        newEnd = 100;
        newStart = 100 - rangeDuration;
      }
      
      onRangeChange(clamp(newStart), clamp(newEnd));
    }
  }, [isDraggingTop, isDraggingBottom, isDraggingRange, containerHeight, startPercent, endPercent, minRangePercent]);

  const handleMouseUp = useCallback(() => {    
    setIsDraggingTop(false);
    setIsDraggingBottom(false);
    setIsDraggingRange(false);
    if (setIsDragging) {
      setIsDragging(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Ensure start is always less than end
  useEffect(() => {
    if (startPercent > 100) {
      return;
    }

    if (endPercent < 0) {
      return;
    }
    
    if (startPercent > endPercent - minRangePercent) {
        onRangeChange(Math.max(0, endPercent - minRangePercent), endPercent);        
    }
    if (endPercent < startPercent + minRangePercent) {
        onRangeChange(startPercent, Math.min(100, startPercent + minRangePercent));        
    }
  }, [startPercent, endPercent, minRangePercent]);


  const selectionHeight = endPercent - startPercent;

  return (
    <div
      className="vertical-range-selector"
      ref={selectorRef}
      style={{ height: `${containerHeight}px` }}
    >
      <div
        className="range-selection"
        style={{
          top: `${startPercent}%`,
          height: `${selectionHeight}%`,
          cursor: disabled ? '' : isDraggingRange ? 'grabbing' : 'grab',
          opacity: disabled ? 0.5 : 1
        }}
        onMouseDown={handleMouseDownRange}
      >
        <div
          className="range-handle top-handle"
          style={{ cursor: disabled ? '' : 'ns-resize' }}
          onMouseDown={handleMouseDownTop}
        />        
        <div
          className="range-handle bottom-handle"
          style={{ cursor: disabled ? '' : 'ns-resize' }}
          onMouseDown={handleMouseDownBottom}
        />
      </div>      
      <div className="range-track before" style={{ height: `${startPercent}%` }} />
      <div className="range-track after" style={{ top: `${endPercent}%`, height: `${100 - endPercent}%` }} />
      <div className='vertical-range-selector-depth-track'>
        <DepthTrack 
                      startDepth={startDepth} 
                      endDepth={endDepth} 
                      depthUnit={depthUnit} 
                      minHeaderHeight={0}
                      headerBottomPadding={0}                      
                      availableHeight={containerHeight - 1}
                      trackTop={trackTop}
                      topMargin={-1}
                      depthMajorIntervals={depthMajorIntervals} 
                      setDialogMajorIntervals={setDialogMajorIntervals}
                      showDepthGridLines={showDepthGridLines}
                      setShowDepthGridlines={setShowDepthGridlines}
                      depthMinorIntervals={depthMinorIntervals}
                      setDepthMinorIntervals={setDepthMinorIntervals}
                      showDepthMinorIntervals={showDepthMinorIntervals}
                      setShowDepthMinorIntervals={setShowDepthMinorIntervals}                      
                      showValueAxisAnnotation={true} />
                      </div>
    </div>
  );
};

export default VerticalRangeSelector;